/**
 * COMPONENT-LEVEL FILL TARGETS — how a shared control registers itself with the help assistant (owner request
 * 2026-10-18; ADR 0072; design §31.9.5 amended).
 *
 * `Input`, `Select` and `Segmented` (this package's `./form.tsx`) call {@link useHelpControl}. Outside a fill scope —
 * every screen but the reviewed ones, every operator console screen — the context is `null` and the hook does nothing.
 * Inside one (the portal's `HelpFillScope`, placed only on reviewed forms: the `help-registry` arch rule), the control
 * hands the scope its name (the field code), its own label, its kind, its choices, how to read it and how to set it
 * through its own native change — so a fill behaves exactly as typing: the form's `onChange`, validation, dirty state
 * and draft follow.
 *
 * NEVER A TARGET, whatever the scope (enforced here, before anything leaves the control, and tested in
 * `help-control.test.tsx`): a password, hidden, file, URL (a website address starts a crawl), email (invitations,
 * contacts) or telephone (another person's number) input; a radio (its group is the target, through `Segmented`); a
 * control whose `autocomplete` is a one-time code, a password or a card field; a control whose name or label holds a
 * secret word; a disabled or read-only control; a control marked `data-help-never`; a control with no name of a code's
 * shape. `Textarea` never registers: a text area is pasted information unless its form binds it itself. The scope then
 * applies the registry's own rules (`isComponentFieldCodeAllowed`) on top.
 *
 * This package imports no help code: the scope is the portal's. Nothing here sends anything.
 */
import { createContext, use, useEffect, useLayoutEffect, useRef } from "react";
export const HelpControlContext = createContext(null);
/** The label a `Field` gives the control inside it. */
export const HelpFieldLabelContext = createContext(null);
/** Input types that are never a fill target. */
export const HELP_CONTROL_NEVER_INPUT_TYPE = Object.freeze(["password", "hidden", "file", "url", "email", "tel", "radio", "submit", "button", "reset", "image", "color", "range", "search"]);
/** `autocomplete` tokens that are never a fill target. */
export const HELP_CONTROL_NEVER_AUTOCOMPLETE = /one-time-code|current-password|new-password|cc-|email|tel|url/iu;
/** Words in a name or label that are never a fill target. */
export const HELP_CONTROL_NEVER_WORD = /password|passcode|secret|token|api[\s_-]?key|iban|card|cvc|cvv|otp|one[\s_-]?time|2fa|recovery|website|e-?mail|phone|webhook/iu;
/** A field code's shape. */
const CODE = /^[a-z][a-z0-9_]{0,63}$/u;
/** Why a control is never a target, or `null` when it may register (the scope's own rules still apply). */
export function helpControlRefusalOf(facts) {
    if (facts.never === true)
        return "marked_never";
    if (facts.name === undefined || !CODE.test(facts.name))
        return "no_code";
    if (facts.disabled === true || facts.readOnly === true)
        return "not_editable";
    if (facts.type !== undefined && HELP_CONTROL_NEVER_INPUT_TYPE.includes(facts.type.toLowerCase()))
        return "never_type";
    if (facts.autoComplete !== undefined && HELP_CONTROL_NEVER_AUTOCOMPLETE.test(facts.autoComplete))
        return "never_autocomplete";
    if (HELP_CONTROL_NEVER_WORD.test(`${facts.name} ${facts.label}`))
        return "never_word";
    return null;
}
const NO_MARK = Object.freeze({});
/**
 * Registers the control while it is mounted inside a fill scope and allowed by {@link helpControlRefusalOf}; answers
 * the attributes to spread on it. `spec` is read on every call, so the scope always sees the latest render.
 */
export function useHelpControl(facts, spec) {
    const port = use(HelpControlContext);
    const latest = useRef(spec);
    useLayoutEffect(() => {
        latest.current = spec;
    });
    const refusal = port === null ? "outside" : helpControlRefusalOf(facts);
    const name = facts.name ?? "";
    const label = facts.label.replace(/\s+/gu, " ").trim();
    // `register` is stable for a scope's life; the port object changes when marks do, which re-renders, never re-registers.
    const register = port?.register;
    useEffect(() => {
        if (register === undefined || refusal !== null)
            return undefined;
        const first = latest.current();
        if (first === null)
            return undefined;
        // Getters: the scope always reads the latest render's kind, ceiling and choices (a select's options change).
        return register({
            name,
            label: label === "" ? name : label,
            get kind() {
                return latest.current()?.kind ?? first.kind;
            },
            get maxLength() {
                return latest.current()?.maxLength;
            },
            get option() {
                return latest.current()?.option;
            },
            read: () => latest.current()?.read() ?? null,
            set: (value) => latest.current()?.set(value),
        });
    }, [register, refusal, name, label]);
    return port === null || refusal !== null ? NO_MARK : port.markOf(name);
}
/**
 * Sets a native control's value the way the browser does when a person types — the element's own value setter, then
 * the `input` and `change` events React listens for — so the form's `onChange` runs. Never focuses, never scrolls.
 */
export function setNativeValue(element, value) {
    if (element === null)
        return;
    const prototype = Object.getPrototypeOf(element);
    const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
    if (setter === undefined)
        return;
    setter.call(element, value);
    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));
}
/** Ticks or unticks a checkbox through its own click (React's `onChange`), only when it differs. */
export function setNativeChecked(element, checked) {
    if (element === null || element.checked === checked)
        return;
    element.click();
}
/** Both refs on one element: the caller's (`ref` is a prop in React 19) and the control's own. */
export function mergedRefOf(own, theirs) {
    return (element) => {
        own.current = element;
        if (typeof theirs === "function")
            theirs(element);
        else if (theirs !== null && typeof theirs === "object" && "current" in theirs)
            theirs.current = element;
    };
}
//# sourceMappingURL=help-control.js.map